import React, { useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Paper,
  Alert,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Grid,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import type {
  Project,
  NodeType,
  Application,
  ProjectValidationError,
  NodeTypeId,
  ApplicationId,
  Node,
} from '../types';
import { ProjectValidationErrorType, formatPrice, calculateProjectPrice, timePriceToMonthlyPrice, NodeRole } from '../types';

interface ProjectEditProps {
  project: Project;
  controlNodes: Record<NodeTypeId, number>;
  workerNodes: Record<NodeTypeId, number>;
  nodes: Node[];
  nodesToDelete: string[];
  onChange: (project: Project) => void;
  onControlNodesChange: (nodes: Record<NodeTypeId, number>) => void;
  onWorkerNodesChange: (nodes: Record<NodeTypeId, number>) => void;
  onNodesToDeleteChange: (nodeIds: string[]) => void;
  nodeTypes: NodeType[];
  applications: Application[];
  validation: ProjectValidationError[];
}

export const ProjectEdit: React.FC<ProjectEditProps> = ({
  project,
  controlNodes,
  workerNodes,
  nodes,
  nodesToDelete,
  onChange,
  onControlNodesChange,
  onWorkerNodesChange,
  onNodesToDeleteChange,
  nodeTypes,
  applications,
  validation,
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [nodeToDelete, setNodeToDelete] = useState<Node | null>(null);

  const activeControlNodes = nodes.filter(n => n.role === NodeRole.CONTROL_NODE && !n.deletedAt && !nodesToDelete.includes(n.id));
  const activeWorkerNodes = nodes.filter(n => n.role === NodeRole.WORKER_NODE && !n.deletedAt && !nodesToDelete.includes(n.id));
  const handleNameChange = (name: string) => {
    onChange({ ...project, name });
  };

  const handleNodeCountChange = (
    nodeTypeId: NodeTypeId,
    count: number,
    isControl: boolean
  ) => {
    if (isControl) {
      const newControlNodes = { ...controlNodes };
      if (count > 0) {
        newControlNodes[nodeTypeId] = count;
      } else {
        delete newControlNodes[nodeTypeId];
      }
      onControlNodesChange(newControlNodes);
    } else {
      const newWorkerNodes = { ...workerNodes };
      if (count > 0) {
        newWorkerNodes[nodeTypeId] = count;
      } else {
        delete newWorkerNodes[nodeTypeId];
      }
      onWorkerNodesChange(newWorkerNodes);
    }
  };

  const handleApplicationToggle = (appId: ApplicationId, checked: boolean) => {
    if (checked) {
      onChange({ ...project, applications: [...project.applications, appId] });
    } else {
      onChange({
        ...project,
        applications: project.applications.filter((id) => id !== appId),
      });
    }
  };

  const handleNodeDeleteClick = (node: Node) => {
    setNodeToDelete(node);
    setDeleteDialogOpen(true);
  };

  const handleNodeDeleteConfirm = () => {
    if (nodeToDelete) {
      onNodesToDeleteChange([...nodesToDelete, nodeToDelete.id]);
    }
    setDeleteDialogOpen(false);
    setNodeToDelete(null);
  };

  const handleNodeDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setNodeToDelete(null);
  };

  const handleNodeUndelete = (nodeId: string) => {
    onNodesToDeleteChange(nodesToDelete.filter(id => id !== nodeId));
  };

  const totalPrice = calculateProjectPrice(project, controlNodes, workerNodes, nodeTypes, applications);

  const nameError = validation.find(v => v.type === ProjectValidationErrorType.NoName);
  const controlNodesError = validation.find(v => v.type === ProjectValidationErrorType.NoControlNodes);
  const highAvailabilityWarning = validation.find(v => v.type === ProjectValidationErrorType.NoHighAvailability);
  const workerNodesError = validation.find(v => v.type === ProjectValidationErrorType.NoWorkerNodes);

  return (
    <Box>
      {/* Project Name */}
      <TextField
        fullWidth
        label="Project Name"
        value={project.name}
        onChange={(e) => handleNameChange(e.target.value)}
        margin="normal"
        required
        error={!!nameError}
        helperText={nameError ? 'Project name is required' : ''}
      />

      {/* Control Nodes */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Control Nodes
        </Typography>
        <Grid container spacing={2}>
          {nodeTypes.map((nodeType) => {
            const provisionedCount = activeControlNodes.filter(n => n.type === nodeType.id).length;
            return (
              <Grid item key={nodeType.id} xs={12} sm={6} md={4}>
                <Box>
                  <Typography variant="subtitle2">{nodeType.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {nodeType.cpu} CPU, {nodeType.ram} GB RAM
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary">
                    {formatPrice(timePriceToMonthlyPrice(nodeType.price))}/month
                  </Typography>
                  <TextField
                    type="number"
                    size="small"
                    value={controlNodes[nodeType.id] || 0}
                    onChange={(e) =>
                      handleNodeCountChange(
                        nodeType.id,
                        parseInt(e.target.value) || 0,
                        true
                      )
                    }
                    inputProps={{ min: provisionedCount }}
                    sx={{ mt: 1 }}
                    fullWidth
                    helperText={provisionedCount > 0 ? `Min: ${provisionedCount} (provisioned)` : ''}
                  />
                </Box>
              </Grid>
            );
          })}
        </Grid>
        {controlNodesError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            At least one control node is required
          </Alert>
        )}
        {highAvailabilityWarning && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            For high availability, at least 3 control nodes are recommended
          </Alert>
        )}
        {(activeControlNodes.length > 0 || nodesToDelete.some(id => {
          const node = nodes.find(n => n.id === id);
          return node && node.role === NodeRole.CONTROL_NODE && !node.deletedAt;
        })) && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Provisioned Control Nodes
            </Typography>
            {nodes
              .filter(n => n.role === NodeRole.CONTROL_NODE && !n.deletedAt)
              .map((node) => {
                const nodeType = nodeTypes.find((nt) => nt.id === node.type);
                const isMarkedForDeletion = nodesToDelete.includes(node.id);
                return (
                  <Box
                    key={node.id}
                    sx={{
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      opacity: isMarkedForDeletion ? 0.5 : 1,
                      textDecoration: isMarkedForDeletion ? 'line-through' : 'none',
                    }}
                  >
                    <Box>
                      <Typography variant="body2">
                        <strong>{node.ip || 'Provisioning...'}</strong> - {nodeType?.name || node.type}
                        {isMarkedForDeletion && (
                          <Typography component="span" variant="caption" color="error" sx={{ ml: 1 }}>
                            (marked for deletion)
                          </Typography>
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        User: {node.user || 'N/A'}
                      </Typography>
                    </Box>
                    {isMarkedForDeletion ? (
                      <Tooltip title="Undo deletion">
                        <IconButton
                          size="small"
                          onClick={() => handleNodeUndelete(node.id)}
                          color="primary"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Delete node">
                        <IconButton
                          size="small"
                          onClick={() => handleNodeDeleteClick(node)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                );
              })}
          </Box>
        )}
      </Paper>

      {/* Worker Nodes */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Worker Nodes
        </Typography>
        <Grid container spacing={2}>
          {nodeTypes.map((nodeType) => {
            const provisionedCount = activeWorkerNodes.filter(n => n.type === nodeType.id).length;
            return (
              <Grid item key={nodeType.id} xs={12} sm={6} md={4}>
                <Box>
                  <Typography variant="subtitle2">{nodeType.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {nodeType.cpu} CPU, {nodeType.ram} GB RAM
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary">
                    {formatPrice(timePriceToMonthlyPrice(nodeType.price))}/month
                  </Typography>
                  <TextField
                    type="number"
                    size="small"
                    value={workerNodes[nodeType.id] || 0}
                    onChange={(e) =>
                      handleNodeCountChange(
                        nodeType.id,
                        parseInt(e.target.value) || 0,
                        false
                      )
                    }
                    inputProps={{ min: provisionedCount }}
                    sx={{ mt: 1 }}
                    fullWidth
                    helperText={provisionedCount > 0 ? `Min: ${provisionedCount} (provisioned)` : ''}
                  />
                </Box>
              </Grid>
            );
          })}
        </Grid>
        {workerNodesError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            At least one worker node is required
          </Alert>
        )}
        {(activeWorkerNodes.length > 0 || nodesToDelete.some(id => {
          const node = nodes.find(n => n.id === id);
          return node && node.role === NodeRole.WORKER_NODE && !node.deletedAt;
        })) && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Provisioned Worker Nodes
            </Typography>
            {nodes
              .filter(n => n.role === NodeRole.WORKER_NODE && !n.deletedAt)
              .map((node) => {
                const nodeType = nodeTypes.find((nt) => nt.id === node.type);
                const isMarkedForDeletion = nodesToDelete.includes(node.id);
                return (
                  <Box
                    key={node.id}
                    sx={{
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      opacity: isMarkedForDeletion ? 0.5 : 1,
                      textDecoration: isMarkedForDeletion ? 'line-through' : 'none',
                    }}
                  >
                    <Box>
                      <Typography variant="body2">
                        <strong>{node.ip || 'Provisioning...'}</strong> - {nodeType?.name || node.type}
                        {isMarkedForDeletion && (
                          <Typography component="span" variant="caption" color="error" sx={{ ml: 1 }}>
                            (marked for deletion)
                          </Typography>
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        User: {node.user || 'N/A'}
                      </Typography>
                    </Box>
                    {isMarkedForDeletion ? (
                      <Tooltip title="Undo deletion">
                        <IconButton
                          size="small"
                          onClick={() => handleNodeUndelete(node.id)}
                          color="primary"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Delete node">
                        <IconButton
                          size="small"
                          onClick={() => handleNodeDeleteClick(node)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                );
              })}
          </Box>
        )}
      </Paper>

      {/* Applications */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Applications
        </Typography>
        <FormControl component="fieldset">
          <FormGroup>
            {applications.map((app) => (
              <Box key={app.id} sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={project.applications.includes(app.id)}
                      onChange={(e) => handleApplicationToggle(app.id, e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">{app.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {app.description} - {formatPrice(timePriceToMonthlyPrice(app.price))}/month
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            ))}
          </FormGroup>
        </FormControl>
      </Paper>

      {/* Total Price */}
      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography variant="body1" align="center">
          Total Monthly Cost: {formatPrice(totalPrice)}
        </Typography>
      </Paper>

      {/* Delete Node Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleNodeDeleteCancel}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" />
          Delete Node
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this node?
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>Warning:</strong> All data on this node will be lost. This action cannot be undone.
          </Alert>
          {nodeToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Node:</strong> {nodeToDelete.ip || nodeToDelete.id}
              </Typography>
              <Typography variant="body2">
                <strong>Type:</strong> {nodeTypes.find(nt => nt.id === nodeToDelete.type)?.name || nodeToDelete.type}
              </Typography>
              <Typography variant="body2">
                <strong>Role:</strong> {nodeToDelete.role.replace('_', ' ')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleNodeDeleteCancel}>Cancel</Button>
          <Button onClick={handleNodeDeleteConfirm} color="error" variant="contained">
            Delete Node
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
